export class NetworkStructure {

    private _nodes: { [nodeId: string]: NetworkNode; } = {};
    private _onNodeAddedCallbacks: ((node: NetworkNode) => void)[] = [];
    private _onLinkAddedCallbacks: ((link: NetworkLink) => void)[] = [];
    private _onNodesRemovedCallbacks: ((nodes: NetworkNode[]) => void)[] = [];
    private _onLinksRemovedCallbacks: ((links: NetworkLink[]) => void)[] = [];
    private _links: { [sourceId: string]: { [targetId: string]: NetworkLink; }; } = {};

    public data: any;

    constructor(data: any = null) {
        this.data = data;
    }

    subscribeOnNodeAdded(callback: (node: NetworkNode) => void) {
        this._onNodeAddedCallbacks.push(callback);
    }

    subscribeOnLinkAdded(callback: (link: NetworkLink) => void) {
        this._onLinkAddedCallbacks.push(callback);
    }

    subscribeOnNodesRemoved(callback: (nodes: NetworkNode[]) => void) {
        this._onNodesRemovedCallbacks.push(callback);
    }

    subscribeOnLinksRemoved(callback: (links: NetworkLink[]) => void) {
        this._onLinksRemovedCallbacks.push(callback);
    }

    public getSourceNodeFromTargetNode(id: string): NetworkNode {
        const keys = Object.keys(this._links);
        const key = keys.filter(x => this._links[x][id])[0];
        return this._nodes[key];
    }

    public findNodeById(id: string): NetworkNode {
        return this._nodes[id];
    }

    public findNodeByText(text: string): NetworkNode {
        const keys = Object.keys(this._nodes);
        const key = keys.filter(key => this._nodes[key].text == text)[0];
        return this._nodes[key];
    }

    public addNode(networkNode: NetworkNode) {
        this._nodes[networkNode.id] = networkNode;
        this._onNodeAddedCallbacks.forEach(callback => {
            callback(this._nodes[networkNode.id]);
        });
    }

    public addLink(link: NetworkLink) {
        if (!this._links[link.sourceId]) {
            this._links[link.sourceId] = {};
        }
        this._links[link.sourceId][link.targetId] = link;
        this._onLinkAddedCallbacks.forEach(callback => {
            callback(this._links[link.sourceId][link.targetId]);
        });
    }

    public removeNode(id: string): NetworkNode[] {
        let removedNodes = [];
        const node = this._nodes[id];
        const keys = Object.keys(this._links);
        const sourceNodeId = keys.filter(x => this._links[x][id])[0];
        let subTreeNodes = [];
        if (node != null) {
            subTreeNodes = this.getSubtree(node);
            removedNodes = subTreeNodes.map(x => x.id);
            subTreeNodes.forEach(n => {
                delete this._links[n.id];
                delete this._nodes[n.id];
            });
            delete this._nodes[id];
            delete this._links[id];
            if (sourceNodeId) {
                delete this._links[sourceNodeId][id];
            }
            removedNodes.push(id);
            subTreeNodes = subTreeNodes.concat([node]);
            this._onNodesRemovedCallbacks.forEach(callback => {
                callback(subTreeNodes);
            });
        }
        return subTreeNodes;
    }

    public getSubtree(node: NetworkNode) {
        const idsLinked = this._links[node.id] ? Object.keys(this._links[node.id]) : [];
        let linkedNodes = idsLinked.map(id => this._nodes[id]);
        linkedNodes.slice(0).forEach(linkedNode => {
            linkedNodes = linkedNodes.concat(this.getSubtree(linkedNode));
        })
        return linkedNodes;
    }

    public getLink(source: string, target: string): NetworkLink {
        return this._links[source][target];
    }

    public getLinks(): NetworkLink[] {
        const temp = [];
        for (const key in this._links) {
            for (const key2 in this._links[key]) {
                temp.push(this._links[key][key2]);
            }
        }
        return temp;
    }

    public getRootNode(): NetworkNode {
        return this.getNodes().filter(x => x.level == 0)[0];
    }

    public getNodes(): NetworkNode[] {
        const temp = [];
        for (const key in this._nodes) {
            temp.push(this._nodes[key]);
        }
        return temp;
    }
}

export class NetworkNode {
    id: string;
    level: number;
    size?: { width: number, height: number }
    data: any;
    text: string;
    color: string;
    fontColor: string;
    fontSize: number;
    fontFace: string;
    fontBackground: string;
    imageSelectedUrl: string;
    shape: string;

    constructor() {
        this.applyDefaults();
    }

    applyDefaults() {
        this.fontColor = 'white';
        this.fontSize = 14;
        this.fontFace = "arial";
        this.fontBackground = "none";
        this.size = null;
    }
}

export class NetworkLink {

    constructor(public sourceId: string, public targetId: string, public text: string = "", public data: any = null) { }
}
